import Highcharts, {
	type AxisLabelsFormatterContextObject,
	type SeriesBarOptions,
	type TooltipFormatterContextObject
} from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import merge from 'lodash/merge';
import React, {
	useCallback,
	useMemo
} from 'react';

import {
	getSharedBarChartStyles,
} from '~/utilities/highcharts';



type SeriesData = {
	color?: string;
	data: SeriesBarOptions['data'];
	enableMouseTracking?: boolean;
	grouping?: boolean;
	showInLegend?: boolean;
	borderRadius?: number;
};

type BarChartProps = {
	/** Data categories (important info for grouping of data) */
	categories: Array<string>;
	/** Height of each bar */
	barHeight?: number,
	/** Chart data */
	data: Array<SeriesData>;
	/** Maximum displayed value */
	maximum: number;
	/** Minimum displayed value */
	minimum?: number;
	/** Possibility to display x-axes which is hidden by default */
	showXAxis?: boolean;
	/** Possibility to set custom tick interval (interval of grey lines bellow chart) */
	tickInterval?: number;
	/** Custom renderer of tooltips */
	tooltipRenderer?: (context: TooltipFormatterContextObject) => string;
	/** Set custom renderer of labels on x-axis according to specs https://api.highcharts.com/highcharts/yAxis.labels.format */
	xAxisLabelRenderer?: (context: AxisLabelsFormatterContextObject) => string;
	/** Enable stacking of bars */
	stacking?: boolean;
};

const BarChart: React.FC<BarChartProps> = (props) => {
	const {
		categories,
		barHeight = 30,
		data,
		maximum,
		minimum = 0,
		showXAxis = false,
		tickInterval,
		tooltipRenderer,
		xAxisLabelRenderer,
		stacking = false,
	} = props;

	const addBackgroundForEveryBar = useCallback(
		(data: Array<SeriesData>): Array<SeriesData> => {
			// we can create background by adding fake bar from 0 to maximum (bellow other stacked bars)
			return [
				{
					enableMouseTracking: false,
					showInLegend: false,
					grouping: false,
					color: 'rgba(0,0,0,0.05)',
					data: Array.from({ length: categories.length }, () => maximum),
				},
				...data
			];
		},
		[
			categories.length,
			maximum
		]
	);

	const applyOpacityToLineColor = useCallback(
		(data: Array<SeriesData>): Array<SeriesData> => {
			return data.map((seriesData) => {
				if (seriesData.color) {
					seriesData.color = new Highcharts.Color(seriesData.color).setOpacity(0.8).get() as string;
				}

				return seriesData;
			});
		},
		[]
	);

	const series = useMemo(
		(): Array<Highcharts.SeriesOptionsType> => {
			let series = data;

			series = applyOpacityToLineColor(series);
			series = addBackgroundForEveryBar(series);

			return series.map((series) => ({
				...series,
				type: 'bar',
				stacking: stacking ? 'normal' : undefined,
			}));
		},
		[
			addBackgroundForEveryBar,
			applyOpacityToLineColor,
			data,
			stacking,
		]
	);

	const chartHeight = useMemo(
		() => {
			let height = barHeight * categories.length;
			if (showXAxis) {
				height += 25;
			}

			return height;
		},
		[
			barHeight,
			categories.length,
			showXAxis,
		]
	);

	const config = useMemo(
		() => {
			const config: Highcharts.Options = {
				chart: {
					backgroundColor: 'transparent',
					height: chartHeight,
					renderTo: 'container',
					spacing: [0, 0, 0, 0],
					margin: showXAxis ? [0, 10, 30, 0] : [0, 10, 0, 0],
					type: 'bar',
				},
				series,
				plotOptions: {
					bar: {
						borderRadius: {
							radius: 4,
							scope: 'point',
							where: 'end',
						}
					},
				},
				tooltip: {
					outside: true,
					followPointer: false,
					formatter: function (this: TooltipFormatterContextObject) {
						if (tooltipRenderer) {
							return tooltipRenderer(this);
						}

						return `${this.x}: ${this.y}`;
					},
				},
				xAxis: {
					categories,
				},
				yAxis: {
					labels: {
						autoRotation: [0],
						formatter: function (this: AxisLabelsFormatterContextObject) {
							if (xAxisLabelRenderer) {
								return xAxisLabelRenderer(this);
							}

							return (this as any).value;
						},
						align: 'center',
					},
					min: minimum,
					max: maximum,
					tickInterval,
				}
			}

			return merge(
				getSharedBarChartStyles(),
				config
			);
		},
		[
			categories,
			chartHeight,
			maximum,
			minimum,
			series,
			showXAxis,
			tickInterval,
			tooltipRenderer,
			xAxisLabelRenderer,
		]
	);

	return (
		<HighchartsReact
			containerProps={{
				className: 'bar-chart',
			}}
			highcharts={Highcharts}
			options={config}
		/>
	);
};

export default BarChart;
